import { enablePointerEvents } from '@utils/pointer-events-control';
import { config, gameState } from '@js/state';
import { getRound, ROUND_BOX_KEYS } from '@utils/roundGenerator';

// ─── Phases ─────────────────────────────────────────────────────────────
const PHASE = {
  IDLE: 'IDLE',
  PARKED_OUT: 'PARKED_OUT',         // outdoor, truck at start, can drive
  DRIVING_IN: 'DRIVING_IN',         // moving toward warehouse
  TRANSITION_TO_INTERIOR: 'TRANSITION_TO_INTERIOR',
  CONVEYOR_INTRO: 'CONVEYOR_INTRO', // crates ride the belt in
  TARGET_DROP: 'TARGET_DROP',       // target number drops onto board
  LOADING: 'LOADING',               // student drags crates onto truck
  TRANSITION_TO_OUTDOOR: 'TRANSITION_TO_OUTDOOR',
  PARKED_LOADED: 'PARKED_LOADED',   // outdoor, truck has cargo
  DRIVING_OUT: 'DRIVING_OUT',       // toward delivery exit
  DELIVERED: 'DELIVERED',
};

const STAGE_W = 1280;
const STAGE_H = 720;

// Outdoor driving lanes — background-1 has Cargo Delivery on the RIGHT.
// Truck enters from off-screen left, drives right to dock, loads, drives
// right past dock; bg swaps to background-3 (post-delivery view) and the
// truck continues right and exits.
const TRUCK_Y_OUT = 400;
const TRUCK_X_START = 220;           // visible on left side of screen at round start
const TRUCK_X_OFF_LEFT = -260;       // off-screen left (used for bg-3 entry)
const TRUCK_X_DOCKED = 975;          // parked in front of Cargo Delivery
const TRUCK_X_EXIT_RIGHT = STAGE_W + 280; // off-screen right
const TRUCK_SPEED = 320;             // px/sec

// Interior layout — based on background-2 (1280x720). Truck is baked into
// the bg with its visual center near (640, 380); crates land at the
// user-specified absolute positions below.
const INTERIOR_TRUCK_X = 640;
const INTERIOR_TRUCK_Y = 380;
const CONVEYOR_Y = 615;       // matches the visible conveyor strip in bg-2
const CRATE_SLOT_X = [180, 380, 580, 780, 980]; // 5 evenly spaced slots
const CRATE_Y = 585;          // resting y for crates on the conveyor

// Truck-bed slot OFFSETS (relative to truck center). Computed from the
// user's absolute landing positions; load order is slot 0 → 1 → 2.
// bringToTop() on each accept means visual depth becomes:
//   slot 0 (lowest depth, drawn first, behind)
//   slot 1 (in-between, drawn on top of slot 0)
//   slot 2 (top depth, drawn on top of all)
//
// Absolute positions (origin 0.5):
//   slot 0 — (692, 409) "lowest depth"
//   slot 1 — (599, 409) "in-between"
//   slot 2 — (639, 346) "top depth"
const TRUCK_BED_SLOTS = [
  { dx: 692 - 640, dy: 409 - 380 }, // (52, 29)  — back-right, lowest
  { dx: 599 - 640, dy: 409 - 380 }, // (-41, 29) — back-left, middle
  { dx: 639 - 640, dy: 346 - 380 }, // (-1, -34) — top stack
];

// Box scale during the interior drop (when crate lands on truck inside warehouse).
const LOADED_BOX_SCALE = 0.81;
// Box scale on the bg-3 leaving stage (truck rendered smaller in this view).
const LEAVING_BOX_SCALE = 0.48;

// bg-3 leaving stage — box slot offsets relative to truck center, derived
// from user-specified absolute positions (208, 336), (264, 336), (231, 298)
// when truck.x = TRUCK_X_START (220), truck.y = TRUCK_Y_OUT (430), then
// shifted by (-75, +80) per design tweak so boxes sit on the flatbed.
const LEAVING_BED_OFFSET_X = -75;
const LEAVING_BED_OFFSET_Y = 80;
const LEAVING_BED_SLOTS = [
  { dx: 208 - 220 + LEAVING_BED_OFFSET_X, dy: 336 - 430 + LEAVING_BED_OFFSET_Y }, // (-87, -14)
  { dx: 264 - 220 + LEAVING_BED_OFFSET_X, dy: 336 - 430 + LEAVING_BED_OFFSET_Y }, // (-31, -14)
  { dx: 231 - 220 + LEAVING_BED_OFFSET_X, dy: 298 - 430 + LEAVING_BED_OFFSET_Y }, // (-64, -52)
];

// bg-3 leaving stage render order, back→front: slot 1, slot 0, slot 2.
// Interior (bg-2) keeps the natural load order — first loaded sits behind,
// each new crate brought to top — so slot 0 deepest, slot 2 on top.
const BED_DRAW_ORDER = [1, 0, 2];

// Conveyor unified speed — boxes AND belt move at exactly this rate (px/sec).
const CONVEYOR_SPEED = 320;

// Top sign final positions (origin 0,0 — top-left corner positioning).
const BOARD_X = 500;
const BOARD_Y = 0;
const PAPER_X = 500;
const PAPER_Y = 124;
const TOP_SIGN_HIDDEN_DY = -300; // off-screen above
const TOP_SIGN_VISIBLE_DY = 0;

// Bulb (progress dot) positions — origin (0.5, 0.5), absolute world coords
// when topSignContainer is at y=0.
const DOT_POSITIONS = [
  { x: 598, y: 107 },
  { x: 640, y: 107 },
  { x: 682, y: 107 },
];

// HUD overlay positions (origin 0.5 on each — sprites are positioned by their
// CENTER over the recesses baked into bg-1).
const PARKED_BADGE_X = 482;
const PARKED_BADGE_Y = 665;
const LEFT_BTN_X = 761;
const LEFT_BTN_Y = 668;
const RIGHT_BTN_X = 874;
const RIGHT_BTN_Y = 668;

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  init() {
    this.phase = PHASE.IDLE;
    this.crateSprites = [];
    this.loadedCrates = [];
    this.truckMoving = false;
    this.driveDirection = 0;
    // True once we've swapped to bg-3 for the leaving stage. While true the
    // HUD (parked badge + arrow buttons) stays hidden because the player has
    // no input — the truck auto-drives off-screen.
    this.isLeavingStage = false;
  }

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    this.cameras.main.fadeIn(400, 0, 0, 0);
    this.cameras.main.setBackgroundColor('#D6F5FF');

    this.createTruckAnimations();
    this.buildOutdoor();
    this.buildInterior();
    this.buildHUD();
    this.setupKeyboard();
    this.startBackgroundMusic();

    this.startRound();
  }

  // ────────────────────────────────────────────────────────────────────
  // Audio — one-shot SFX + looping cues (engine, conveyor) + bg music.
  // Sounds are added once and reused; keys are loaded in LoadScene.
  // ────────────────────────────────────────────────────────────────────
  startBackgroundMusic() {
    if (this.bgMusic || !this.cache.audio.exists('bg-music')) return;
    this.bgMusic = this.sound.add('bg-music', { volume: 0.15, loop: true });
    this.bgMusic.play();
  }

  // One-shot SFX — re-uses a single instance per key, restarting on replay.
  playSfx(key, volume = 1) {
    if (!this.cache.audio.exists(key)) return null;
    if (!this._sfx) this._sfx = {};
    let sound = this._sfx[key];
    if (!sound) {
      sound = this.sound.add(key, { volume });
      this._sfx[key] = sound;
    }
    sound.stop();
    sound.play({ volume });
    return sound;
  }

  // Looping cue (engine, conveyor) — idempotent start; safe to call repeatedly.
  startLoop(key, volume = 0.4) {
    if (!this.cache.audio.exists(key)) return null;
    if (!this._loops) this._loops = {};
    let sound = this._loops[key];
    if (!sound) {
      sound = this.sound.add(key, { volume, loop: true });
      this._loops[key] = sound;
    }
    // Cancel any in-flight fade-out so a quick restart snaps back to full
    // volume instead of continuing to fade toward silence.
    this.tweens.killTweensOf(sound);
    sound.setVolume(volume);
    if (!sound.isPlaying) sound.play({ volume, loop: true });
    return sound;
  }

  // Stop a looping cue with a short volume fade-out so it eases off rather
  // than cutting out abruptly (e.g. the truck engine winding down on arrival).
  stopLoop(key, fadeMs = 400) {
    const sound = this._loops && this._loops[key];
    if (!sound || !sound.isPlaying) return;
    const restoreVolume = sound.volume;
    this.tweens.killTweensOf(sound);
    this.tweens.add({
      targets: sound,
      volume: 0,
      duration: fadeMs,
      ease: 'Sine.easeOut',
      onComplete: () => {
        sound.stop();
        sound.setVolume(restoreVolume); // reset for the next play
      },
    });
  }

  createTruckAnimations() {
    // Empty truck has TWO loops:
    //   • 'truck-idle-empty'  → truckStillAnim — played when parked.
    //   • 'truck-moving-empty' → truckInitialAnim — played while driving.
    // We swap between them on PARKED_OUT ↔ DRIVING_IN transitions.
    if (this.textures.exists('truck-still') && !this.anims.exists('truck-idle-empty')) {
      this.anims.create({
        key: 'truck-idle-empty',
        frames: this.anims.generateFrameNames('truck-still', {
          start: 0, end: 19, prefix: 'truckStillAnim', suffix: '.png',
        }),
        frameRate: 12,
        repeat: -1,
      });
    } else if (this.textures.exists('truck-initial') && !this.anims.exists('truck-idle-empty')) {
      // Fallback to the older atlas if truck-still isn't loaded.
      this.anims.create({
        key: 'truck-idle-empty',
        frames: this.anims.generateFrameNames('truck-initial', {
          start: 0, end: 19, prefix: 'truckInitialAnim', suffix: '.png',
        }),
        frameRate: 12,
        repeat: -1,
      });
    }
    if (this.textures.exists('truck-initial') && !this.anims.exists('truck-moving-empty')) {
      this.anims.create({
        key: 'truck-moving-empty',
        frames: this.anims.generateFrameNames('truck-initial', {
          start: 0, end: 19, prefix: 'truckInitialAnim', suffix: '.png',
        }),
        frameRate: 12,
        repeat: -1,
      });
    }
    if (this.textures.exists('truck-final') && !this.anims.exists('truck-idle-loaded')) {
      this.anims.create({
        key: 'truck-idle-loaded',
        frames: this.anims.generateFrameNames('truck-final', {
          start: 0, end: 59, prefix: 'truckFinalAnim', suffix: '.png',
        }),
        frameRate: 24,
        repeat: -1,
      });
    }
    if (this.textures.exists('truck-window') && !this.anims.exists('truck-window')) {
      this.anims.create({
        key: 'truck-window',
        frames: this.anims.generateFrameNames('truck-window', {
          start: 0, end: 34, prefix: 'windowAnim', suffix: '.png',
        }),
        frameRate: 24,
        repeat: 0,
      });
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Outdoor scene — single background image; only the dynamic truck overlays.
  // ────────────────────────────────────────────────────────────────────
  buildOutdoor() {
    this.outdoorContainer = this.add.container(0, 0);
    this.outdoorContainer.setDepth(10);

    // Swappable outdoor background. Default is bg-out (approach + dock view);
    // we swap its texture to bg-out-leaving for the post-delivery animation.
    this.outdoorBg = this.add.image(STAGE_W / 2, STAGE_H / 2, 'bg-out');
    this.fitCover(this.outdoorBg, STAGE_W, STAGE_H);
    this.outdoorContainer.add(this.outdoorBg);

    // Dynamic truck — sprite playing the idle animations.
    // The atlas frames are trimmed against a 1280x720 sourceSize where the
    // truck visual sits at (361..871, 271..527). Setting origin to that
    // visual center makes sprite.x/sprite.y line up with the truck's
    // apparent center, so tweening sprite.x moves the truck cleanly.
    if (this.anims.exists('truck-idle-empty')) {
      // Use truck-still as the initial texture if available (the new still
      // anim) — otherwise fall back to truck-initial so an existing build
      // without truckStillAnim still renders.
      const stillKey = this.textures.exists('truck-still') ? 'truck-still' : 'truck-initial';
      const stillFrame = stillKey === 'truck-still'
        ? 'truckStillAnim0.png'
        : 'truckInitialAnim0.png';
      this.truck = this.add
        .sprite(TRUCK_X_START, TRUCK_Y_OUT, stillKey, stillFrame)
        .setOrigin(616 / 1280, 398 / 720);
      this.truck.play('truck-idle-empty');
    } else {
      // Static-image fallback if the atlases didn't load
      this.truck = this.add.image(TRUCK_X_START, TRUCK_Y_OUT, 'truck').setOrigin(0.5);
      this.fitInto(this.truck, 380, 200);
    }
    this.outdoorContainer.add(this.truck);

    // Truck-bed crates ride along with the truck — same x as truck.
    this.truckBedContainer = this.add.container(TRUCK_X_START, TRUCK_Y_OUT);
    this.outdoorContainer.add(this.truckBedContainer);
  }

  setOutdoorBg(key) {
    if (this.outdoorBg && this.textures.exists(key)) {
      this.outdoorBg.setTexture(key);
      this.fitCover(this.outdoorBg, STAGE_W, STAGE_H);
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Interior scene — single background image; conveyor scrolls and crates
  // ride in on top, target sign drops, crates loaded onto static interior truck.
  // ────────────────────────────────────────────────────────────────────
  buildInterior() {
    this.interiorContainer = this.add.container(0, 0);
    this.interiorContainer.setDepth(20).setVisible(false).setAlpha(0);

    const bg = this.add.image(STAGE_W / 2, STAGE_H / 2, 'bg-in');
    this.fitCover(bg, STAGE_W, STAGE_H);
    this.interiorContainer.add(bg);

    // Conveyor scroll texture overlaid on the conveyor region of bg-in
    if (this.textures.exists('belt')) {
      const tex = this.textures.get('belt').getSourceImage();
      this.conveyorBelt = this.add
        .tileSprite(STAGE_W / 2, CONVEYOR_Y, STAGE_W, tex.height, 'belt')
        .setOrigin(0.5);
      this.interiorContainer.add(this.conveyorBelt);
    }

    // Top sign — board (cables + 3 baked dots) at (500,0) + board-paper (white
    // panel for target number) at (500,124), both origin (0,0). The whole
    // group hides above the screen (y = -300) and tweens down to y = 0.
    this.topSignContainer = this.add.container(0, TOP_SIGN_HIDDEN_DY);

    const board = this.add.image(BOARD_X, BOARD_Y, 'board').setOrigin(0, 0);
    this.topSignContainer.add(board);

    const paper = this.add.image(PAPER_X, PAPER_Y, 'board-paper').setOrigin(0, 0);
    this.topSignContainer.add(paper);

    // Target number — centered on board-paper (paper is 280x142 at top-left 500,124)
    const paperCenterX = PAPER_X + 280 / 2; // 640
    const paperCenterY = PAPER_Y + 142 / 2; // 195
    this.targetText = this.add
      .text(paperCenterX, paperCenterY, '', {
        fontFamily: 'Athletics',
        fontSize: '78px',
        color: '#2B2B2B',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.topSignContainer.add(this.targetText);

    // Progress dots — fixed world positions (598,131), (640,131), (682,131)
    // origin (0.5, 0.5). The container is at (0,0) so we use absolute coords.
    this.dotsContainer = this.add.container(0, 0);
    this.topSignContainer.add(this.dotsContainer);

    this.interiorContainer.add(this.topSignContainer);

    // Container for crates loaded ONTO the static interior truck (decorative)
    this.interiorTruckBedContainer = this.add.container(INTERIOR_TRUCK_X, INTERIOR_TRUCK_Y);
    this.interiorContainer.add(this.interiorTruckBedContainer);

    // Tail-light blinkers — Figma node 202:10. Drawn at low opacity by
    // default; flashBlinkers() pulses them to alpha 1 on a wrong drop.
    this.blinkers = [];
    if (this.textures.exists('blinker')) {
      [{ x: 514, y: 470 }, { x: 765, y: 470 }].forEach(({ x, y }) => {
        const b = this.add
          .image(x, y, 'blinker')
          .setOrigin(0.5)
          .setDisplaySize(78, 50)
          .setAlpha(0.2);
        this.interiorContainer.add(b);
        this.blinkers.push(b);
      });
    }

    // Crates layer — TOPMOST so dragged crate floats above truck/board/everything
    this.cratesLayer = this.add.container(0, 0);
    this.interiorContainer.add(this.cratesLayer);
  }

  // ────────────────────────────────────────────────────────────────────
  // HUD — overlays buttons + parked badge over the bg's HUD strip.
  // ────────────────────────────────────────────────────────────────────
  buildHUD() {
    this.hudContainer = this.add.container(0, 0).setDepth(100);

    // Parked text overlay (green/grey image swap), origin 0.5 at exact coord
    this.parkedBadge = this.add
      .image(PARKED_BADGE_X, PARKED_BADGE_Y, 'parked-on')
      .setOrigin(0.5);
    this.hudContainer.add(this.parkedBadge);

    // Arrow buttons, origin 0.5 at exact coords
    this.leftBtn = this.makeArrowButton(LEFT_BTN_X, LEFT_BTN_Y, 'left');
    this.rightBtn = this.makeArrowButton(RIGHT_BTN_X, RIGHT_BTN_Y, 'right');
    this.hudContainer.add(this.leftBtn);
    this.hudContainer.add(this.rightBtn);

    // Start hidden; setPhase reveals what each phase needs.
    this.setHudVisible(false, 'hidden');
  }

  // buttonState: 'active' | 'dim' | 'hidden'
  //  - active: full opacity, interactive (player can drive)
  //  - dim:    visible at low opacity, disabled (mid-drive feedback)
  //  - hidden: setVisible(false) (next stage / interior)
  setHudVisible(badgeVisible, buttonState) {
    if (!this.parkedBadge) return;
    this.parkedBadge.setVisible(badgeVisible);
    this.setButtonState(this.leftBtn, buttonState);
    this.setButtonState(this.rightBtn, buttonState);
  }

  setButtonState(btn, state) {
    if (!btn) return;
    if (state === 'hidden') {
      btn.setVisible(false);
      btn.disableInteractive();
      btn.setScale(1);
      return;
    }
    btn.setVisible(true);
    if (state === 'active') {
      btn.setAlpha(1);
      if (!btn.input || !btn.input.enabled) btn.setInteractive({ useHandCursor: true });
    } else {
      // 'dim' — visible at low alpha, disabled
      btn.setAlpha(0.45);
      btn.disableInteractive();
      btn.setScale(1);
    }
  }

  makeArrowButton(x, y, dir) {
    // Origin 0.5 — the button sprite sits centered on the given coord.
    const key = dir === 'left' ? 'left-button' : 'right-button';
    const img = this.add.image(x, y, key).setOrigin(0.5);
    img.setInteractive({ useHandCursor: true });
    img.dirSign = dir === 'left' ? -1 : 1;
    img.on('pointerdown', () => {
      this.driveDirection = img.dirSign;
      img.setScale(0.92);
    });
    img.on('pointerup', () => {
      this.driveDirection = 0;
      img.setScale(1);
    });
    img.on('pointerout', () => {
      if (this.driveDirection === img.dirSign) this.driveDirection = 0;
      img.setScale(1);
    });
    return img;
  }

  setupKeyboard() {
    this.cursors = this.input.keyboard.createCursorKeys();
  }

  setParked(on) {
    if (!this.textures.exists('parked-on') || !this.textures.exists('parked-off')) return;
    this.parkedBadge.setTexture(on ? 'parked-on' : 'parked-off');
    // When lighting up, give it a brief scale pop so the change is noticed.
    if (on && this.parkedBadge.visible) {
      this.playSfx('sfx-park-ding', 0.5);
      this.tweens.killTweensOf(this.parkedBadge);
      this.parkedBadge.setScale(1);
      this.tweens.add({
        targets: this.parkedBadge,
        scale: { from: 1.2, to: 1 },
        duration: 280,
        ease: 'Back.easeOut',
      });
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Round flow
  // ────────────────────────────────────────────────────────────────────
  setPhase(p) {
    this.phase = p;
    // bg-3 leaving stage is fully automatic — no HUD because the player has
    // no input. Suppress every HUD element until the next round resets us.
    if (this.isLeavingStage) {
      this.setHudVisible(false, 'hidden');
      return;
    }
    // Parked badge — visible during any outdoor phase so the parked-enabled
    // texture is on screen when the truck arrives at the dock.
    const isOutdoor =
      p === PHASE.PARKED_OUT ||
      p === PHASE.DRIVING_IN ||
      p === PHASE.PARKED_LOADED ||
      p === PHASE.DRIVING_OUT ||
      p === PHASE.DELIVERED;
    // Buttons:
    //   active  → player can drive (PARKED_OUT, PARKED_LOADED)
    //   dim     → mid-drive feedback (DRIVING_IN, DRIVING_OUT)
    //   hidden  → off-screen for any other stage (interior + transitions)
    let buttonState = 'hidden';
    if (p === PHASE.PARKED_OUT || p === PHASE.PARKED_LOADED) buttonState = 'active';
    else if (p === PHASE.DRIVING_IN || p === PHASE.DRIVING_OUT) buttonState = 'dim';
    this.setHudVisible(isOutdoor, buttonState);
  }

  startRound() {
    const round = getRound(gameState.roundIndex);
    gameState.target = round.target;
    gameState.correctCount = round.correctCount;
    gameState.loadedCount = 0;
    this.currentRound = round;

    this.loadedCrates = [];
    this.truckBedContainer.removeAll(true);
    this.interiorTruckBedContainer.removeAll(true);
    // Clear any leftover crates from the previous round so they don't flash
    // when the interior fades in for this round.
    if (this.cratesLayer) this.cratesLayer.removeAll(true);

    // Each round begins with bg-1, truck VISIBLE on the left side of the
    // road, parked-faded shown. Player presses → to drive to the dock.
    this.isLeavingStage = false;
    this.setOutdoorBg('bg-out');
    this.truck.setPosition(TRUCK_X_START, TRUCK_Y_OUT);
    if (this.anims.exists('truck-idle-empty') && this.truck.play) {
      this.truck.play('truck-idle-empty');
    }
    this.truckBedContainer.setPosition(TRUCK_X_START, TRUCK_Y_OUT);

    this.outdoorContainer.setVisible(true).setAlpha(1);
    this.interiorContainer.setVisible(false).setAlpha(0);
    this.topSignContainer.y = TOP_SIGN_HIDDEN_DY;

    this.setParked(false); // parked-faded — truck not yet docked
    this.setPhase(PHASE.PARKED_OUT);
  }

  driveTo(targetX, onArrive) {
    const distance = Math.abs(targetX - this.truck.x);
    const duration = (distance / TRUCK_SPEED) * 1000;
    this.truckMoving = true;
    this.startLoop('sfx-engine', 0.75);

    // Swap to the moving loop (original truckInitialAnim) for the duration
    // of the tween; restore the still loop on arrival. Loaded truck has a
    // single anim (truck-idle-loaded), so this swap only applies to empty.
    const movingKey = 'truck-moving-empty';
    const stillKey = 'truck-idle-empty';
    const isEmpty = this.anims.exists(movingKey)
      && this.truck.anims
      && this.truck.anims.currentAnim
      && this.truck.anims.currentAnim.key === stillKey;
    if (isEmpty) {
      this.truck.play(movingKey);
    }

    this.tweens.add({
      targets: [this.truck, this.truckBedContainer],
      x: targetX,
      duration,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.truckMoving = false;
        this.stopLoop('sfx-engine');
        if (isEmpty && this.anims.exists(stillKey)) {
          this.truck.play(stillKey);
        }
        if (onArrive) onArrive();
      },
    });
  }

  transitionToInterior() {
    this.setPhase(PHASE.TRANSITION_TO_INTERIOR);
    this.interiorContainer.setVisible(true).setAlpha(0);
    this.tweens.add({
      targets: this.outdoorContainer,
      alpha: 0,
      duration: 600,
    });
    this.tweens.add({
      targets: this.interiorContainer,
      alpha: 1,
      duration: 600,
      onComplete: () => {
        this.outdoorContainer.setVisible(false);
        this.runConveyorIntro();
      },
    });
  }

  runConveyorIntro() {
    // Conveyor + crates move TOGETHER at the same px/sec. Crates start spaced
    // 200 apart, all off-screen left, the rightmost just past the left edge
    // so it appears first. As the belt scrolls right, each crate enters the
    // visible area in sequence and they all settle into their slots
    // simultaneously when the tween finishes.
    this.setPhase(PHASE.CONVEYOR_INTRO);
    this.spawnCrates();
    this.conveyorActive = true;
    this.startLoop('sfx-conveyor', 0.4);

    // Travel distance — every crate moves the SAME amount, ending at its slot.
    const travelDistance = 1000;
    this.crateSprites.forEach((c, i) => {
      c.x = CRATE_SLOT_X[i] - travelDistance;
    });

    const duration = (travelDistance / CONVEYOR_SPEED) * 1000;
    this.tweens.add({
      targets: this.crateSprites,
      x: '+=' + travelDistance,
      duration,
      ease: 'Linear',
      onComplete: () => {
        this.conveyorActive = false;
        this.stopLoop('sfx-conveyor');
        this.dropTarget();
      },
    });
  }

  spawnCrates() {
    this.cratesLayer.removeAll(true);
    this.crateSprites = [];
    const crates = this.currentRound.crates;
    if (crates.length !== 5) {
      console.warn(
        `[truck-loading] expected 5 crates for round, got ${crates.length}`,
        crates,
      );
    }
    crates.forEach((crate, i) => {
      const c = this.makeCrateSprite(crate, i);
      this.cratesLayer.add(c);
      this.crateSprites.push(c);
    });
  }

  makeCrateSprite(crate, index) {
    // Each round has its own per-crate sprite key — the multiplication
    // expression is baked into the PNG, so no code-drawn label.
    const spriteKey = crate.key;
    const c = this.add.container(CRATE_SLOT_X[index], CRATE_Y);

    let baseImg;
    if (this.textures.exists(spriteKey)) {
      baseImg = this.add.image(0, 0, spriteKey).setOrigin(0.5);
      this.fitInto(baseImg, 140, 100);
    } else {
      // Fallback if the per-round sprite is missing — code-drawn box + label
      // so the game still runs while the asset is being exported/added.
      console.warn('[crate] missing sprite key', spriteKey, 'using fallback');
      baseImg = this.add
        .rectangle(0, 0, 130, 92, 0xffcc4c)
        .setStrokeStyle(4, 0xb25900);
      c.add(baseImg);
      const fallbackLabel = this.add
        .text(0, 0, `${crate.a} × ${crate.b}`, {
          fontFamily: 'Athletics',
          fontSize: '24px',
          color: '#2B2B2B',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      c.add(fallbackLabel);
    }
    if (this.textures.exists(spriteKey)) c.add(baseImg);

    // Drag setup — interactivity on the image (or fallback rectangle)
    baseImg.setInteractive({ useHandCursor: true, draggable: true });

    c.crateData = { ...crate, index, originalX: CRATE_SLOT_X[index], originalY: CRATE_Y };
    let pickOffsetX = 0;
    let pickOffsetY = 0;

    baseImg.on('dragstart', pointer => {
      if (this.phase !== PHASE.LOADING) return;
      pickOffsetX = c.x - pointer.x;
      pickOffsetY = c.y - pointer.y;
      // Drag preview matches the dropped/loaded scale so the crate doesn't
      // jump in size when it lands on the truck bed.
      c.setScale(LOADED_BOX_SCALE);
      this.cratesLayer.bringToTop(c);
    });
    baseImg.on('drag', pointer => {
      if (this.phase !== PHASE.LOADING) return;
      c.x = pointer.x + pickOffsetX;
      c.y = pointer.y + pickOffsetY;
    });
    baseImg.on('dragend', pointer => {
      if (this.phase !== PHASE.LOADING) return;
      this.handleCrateDrop(c, pointer);
    });

    return c;
  }

  dropTarget() {
    this.setPhase(PHASE.TARGET_DROP);
    this.targetText.setText(String(gameState.target));
    this.buildDots(gameState.correctCount);

    this.tweens.add({
      targets: this.topSignContainer,
      y: TOP_SIGN_VISIBLE_DY,
      duration: 700,
      ease: 'Bounce.easeOut',
      onComplete: () => this.setPhase(PHASE.LOADING),
    });
  }

  buildDots(count) {
    this.dotsContainer.removeAll(true);
    // Up to 3 fixed dot slots — render only `count` of them (one per correct
    // crate the round expects).
    const slots = DOT_POSITIONS.slice(0, Math.min(count, DOT_POSITIONS.length));
    slots.forEach((pos, i) => {
      let dot;
      if (this.textures.exists('bulb-red')) {
        dot = this.add.image(pos.x, pos.y, 'bulb-red').setOrigin(0.5, 0.5);
      } else {
        dot = this.add.circle(pos.x, pos.y, 12, 0xff5252, 1);
      }
      dot.dotIndex = i;
      this.dotsContainer.add(dot);
    });
  }

  lightDot(idx) {
    const dot = this.dotsContainer.list[idx];
    if (!dot) return;
    if (this.textures.exists('bulb-green')) {
      dot.setTexture('bulb-green');
    } else if (dot.setFillStyle) {
      dot.setFillStyle(0x00e573, 1);
    }
  }

  // ─── Drag/drop handling ───
  handleCrateDrop(c, pointer) {
    // Truck zone in interior coords (where the static truck sits in bg-2)
    const truckZone = new Phaser.Geom.Rectangle(
      INTERIOR_TRUCK_X - 200,
      INTERIOR_TRUCK_Y - 70,
      400,
      160,
    );
    const onTruck = Phaser.Geom.Rectangle.Contains(truckZone, pointer.x, pointer.y);

    if (onTruck && c.crateData.isCorrect) {
      this.acceptCrate(c);
    } else if (onTruck && !c.crateData.isCorrect) {
      this.rejectCrate(c);
    } else {
      this.tweenBack(c);
    }
  }

  acceptCrate(c) {
    c.dragHandle = null;
    if (c.list[0] && c.list[0].disableInteractive) c.list[0].disableInteractive();
    // Drag scale already equals LOADED_BOX_SCALE — no reset needed; the
    // tween below preserves it.
    const slotIdx = gameState.loadedCount;
    const slot = TRUCK_BED_SLOTS[Math.min(slotIdx, TRUCK_BED_SLOTS.length - 1)];
    const targetX = INTERIOR_TRUCK_X + slot.dx;
    const targetY = INTERIOR_TRUCK_Y + slot.dy;
    this.playSfx('sfx-whoosh', 0.5);
    this.tweens.add({
      targets: c,
      x: targetX,
      y: targetY,
      scale: LOADED_BOX_SCALE,
      duration: 380,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        this.playSfx('sfx-clunk', 0.6);
        this.lightDot(gameState.loadedCount);
        gameState.loadedCount += 1;
        this.smallConfetti(targetX, targetY);
        // Interior depth: each newly-loaded crate sits ON TOP of previously
        // loaded ones (so slot 0 is deepest, slot 2 ends up on top).
        this.cratesLayer.bringToTop(c);
        this.loadedCrates.push(c);
        if (gameState.loadedCount >= gameState.correctCount) {
          this.playSfx('sfx-correct', 0.6);
          this.time.delayedCall(700, () => this.transitionToOutdoorLoaded());
        }
      },
    });
  }

  rejectCrate(c) {
    gameState.mistakes += 1;
    this.playSfx('sfx-wrong', 0.6);
    // Wrong drop: shake in place + flash tail-lights. Crate only returns
    // to its belt slot AFTER the blink sequence finishes (so the player
    // gets enough visual feedback that the drop was rejected).
    const blinkDurationMs = 1000;
    this.flashBlinkers(blinkDurationMs);

    const baseX = c.x;
    let i = 0;
    const vibrate = () => {
      i++;
      if (i > 8) return;            // shake settles; crate stays put
      c.x = baseX + (i % 2 === 0 ? -8 : 8);
      this.time.delayedCall(40, vibrate);
    };
    vibrate();

    this.time.delayedCall(blinkDurationMs, () => {
      c.x = baseX;
      this.tweenBack(c);
    });
  }

  // Blinker tail-lights — Figma node 202:10. Pulses the persistent blinker
  // sprites (built in buildInterior at alpha 0.2) up to alpha 1 on a wrong
  // drop, then settles back to 0.2.
  flashBlinkers(totalMs = 1000) {
    if (!this.blinkers || this.blinkers.length === 0) return;
    // Cancel any in-flight flash so a second wrong drop restarts cleanly.
    this.blinkers.forEach(b => this.tweens.killTweensOf(b));

    const halfCycle = Math.max(90, Math.floor(totalMs / 8));
    this.blinkers.forEach(b => {
      b.setAlpha(0.2);
      this.tweens.add({
        targets: b,
        alpha: 1,
        duration: halfCycle,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.easeInOut',
        onComplete: () => b.setAlpha(0.2),
      });
    });
  }

  tweenBack(c) {
    this.tweens.add({
      targets: c,
      x: c.crateData.originalX,
      y: c.crateData.originalY,
      scale: 1,
      duration: 320,
      ease: 'Cubic.easeOut',
    });
  }

  smallConfetti(x, y) {
    const colors = [0xff5252, 0x40f098, 0x33ccff, 0xffcc4c, 0xffb700];
    for (let i = 0; i < 14; i++) {
      const piece = this.add
        .rectangle(x, y, 8, 4, colors[i % colors.length])
        .setDepth(50)
        .setRotation(Math.random() * Math.PI * 2);
      this.interiorContainer.add(piece);
      const tx = x + (Math.random() - 0.5) * 200;
      const ty = y - 80 - Math.random() * 80;
      this.tweens.add({
        targets: piece,
        x: tx,
        y: ty,
        rotation: piece.rotation + Math.PI * 2 * (Math.random() - 0.5),
        alpha: 0,
        duration: 700 + Math.random() * 300,
        ease: 'Cubic.easeOut',
        onComplete: () => piece.destroy(),
      });
    }
  }

  // ─── Interior → outdoor (loaded) ───
  transitionToOutdoorLoaded() {
    this.setPhase(PHASE.TRANSITION_TO_OUTDOOR);

    this.renderOutdoorBedFromLoaded();

    // Pre-stage the leaving view (bg-3, truck on left, driver-visible idle)
    // BEFORE the fade-in so we never flash the dock view from bg-1 when
    // outdoor becomes visible again. Mark leaving so HUD stays hidden.
    this.isLeavingStage = true;
    this.setOutdoorBg('bg-out-leaving');
    this.truck.setPosition(TRUCK_X_START, TRUCK_Y_OUT);

    // Truck holds on a static frame (no animation) until the window-roll
    // delay elapses. We freeze on truck-still's first frame so it reads as
    // a parked truck rather than a stale loaded loop.
    if (this.truck.anims) this.truck.anims.stop();
    if (this.textures.exists('truck-still')) {
      this.truck.setTexture('truck-still', 'truckStillAnim0.png');
    }
    this.truckBedContainer.setPosition(TRUCK_X_START, TRUCK_Y_OUT);

    this.outdoorContainer.setVisible(true).setAlpha(0);
    this.tweens.add({ targets: this.interiorContainer, alpha: 0, duration: 600 });
    this.tweens.add({
      targets: this.outdoorContainer,
      alpha: 1,
      duration: 600,
      onComplete: () => {
        this.interiorContainer.setVisible(false);
        this.topSignContainer.y = TOP_SIGN_HIDDEN_DY;
        this.setParked(true);
        this.setPhase(PHASE.PARKED_LOADED);
      },
    });

    // Schedule the window-roll-down 1500ms after the transition starts —
    // gives the player a beat to register the loaded truck before the
    // driver rolls down the window and pulls away.
    this.time.delayedCall(1500, () => {
      if (this.phase !== PHASE.PARKED_LOADED) return;

      const continueAfterWindow = () => {
        if (this.anims.exists('truck-idle-loaded') && this.truck.play) {
          this.truck.play('truck-idle-loaded');
        }
        this.time.delayedCall(300, () => {
          if (this.phase === PHASE.PARKED_LOADED) this.startDriveOut();
        });
      };

      if (this.anims.exists('truck-window') && this.truck.play) {
        this.truck.play('truck-window');
        this.truck.once('animationcomplete-truck-window', continueAfterWindow);
      } else {
        // No window anim available — fall straight through to drive-out.
        continueAfterWindow();
      }
    });
  }

  renderOutdoorBedFromLoaded() {
    // bg-3 leaving stage — boxes use LEAVING_BED_SLOTS offsets at LEAVING
    // scale. Render order back→front is BED_DRAW_ORDER (slot 1, 0, 2) so
    // slot 2 sits on top.
    this.truckBedContainer.removeAll(true);
    // Reset jiggle state — we don't start the per-box bob here. The cargo
    // sits perfectly still while the truck is parked / driver rolls down
    // the window; the bob is created in startDriveOut() once the truck
    // actually starts moving.
    if (this.boxBobTweens) {
      this.boxBobTweens.forEach(t => t.stop());
    }
    this.boxBobTweens = [];
    this.leavingBoxSprites = [];

    BED_DRAW_ORDER.forEach((slotIdx) => {
      const crate = this.loadedCrates[slotIdx];
      if (!crate) return; // round 3 has only 2 correct → no slot 2 crate
      const slot = LEAVING_BED_SLOTS[slotIdx];
      const spriteKey = crate.crateData.key;
      let img;
      if (this.textures.exists(spriteKey)) {
        img = this.add.image(slot.dx, slot.dy, spriteKey).setOrigin(0.5);
        img.setScale(LEAVING_BOX_SCALE);
      } else {
        img = this.add
          .rectangle(slot.dx, slot.dy, 110, 80, 0xffcc4c)
          .setStrokeStyle(3, 0xb25900)
          .setScale(LEAVING_BOX_SCALE);
      }
      this.truckBedContainer.add(img);
      this.leavingBoxSprites.push({ img, baseDy: slot.dy });
    });
  }

  startLeavingBoxJiggle() {
    if (!this.leavingBoxSprites || this.leavingBoxSprites.length === 0) return;
    if (this.boxBobTweens) {
      this.boxBobTweens.forEach(t => t.stop());
    }
    this.boxBobTweens = [];

    // Per-box jiggle on top of the truck's main bob — small amplitude
    // (2px) + slight phase offset per slot so the cargo reads as loose
    // weight bouncing on the bed, not fused to the truck.
    const BOX_BOB_AMP = 2;
    this.leavingBoxSprites.forEach(({ img, baseDy }, drawIdx) => {
      const tween = this.tweens.add({
        targets: img,
        y: { from: baseDy, to: baseDy - BOX_BOB_AMP },
        duration: 220 + drawIdx * 40, // 220 / 260 / 300 ms per box
        delay: drawIdx * 80,          // staggered start
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.boxBobTweens.push(tween);
    });
  }

  // ─── Drive out (single stage on bg-3) ─────────────────────────────
  startDriveOut() {
    if (this.phase !== PHASE.PARKED_LOADED) return;
    this.setPhase(PHASE.DRIVING_OUT);
    this.setParked(false);
    this.truckMoving = true;
    this.startLoop('sfx-engine', 0.75);
    // Cargo only starts jiggling once the truck is actually rolling.
    this.startLeavingBoxJiggle();
    const distance = TRUCK_X_EXIT_RIGHT - this.truck.x;
    const duration = (distance / TRUCK_SPEED) * 1000;

    // Slight up-and-down bob while the truck drives off — gives the cargo a
    // sense of weight bouncing on the road. Yoyo tween on y for both the
    // truck and the bed so loaded crates ride along.
    const baseY = this.truck.y;
    const BOB_AMPLITUDE = 4;     // px — small enough to read as suspension play
    const BOB_DURATION = 260;    // ms per half-cycle
    this.driveOutBob = this.tweens.add({
      targets: [this.truck, this.truckBedContainer],
      y: { from: baseY, to: baseY - BOB_AMPLITUDE },
      duration: BOB_DURATION,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.tweens.add({
      targets: [this.truck, this.truckBedContainer],
      x: TRUCK_X_EXIT_RIGHT,
      duration,
      ease: 'Sine.easeIn',
      onComplete: () => {
        if (this.driveOutBob) {
          this.driveOutBob.stop();
          this.driveOutBob = null;
          this.truck.y = baseY;
          this.truckBedContainer.y = baseY;
        }
        this.truckMoving = false;
        this.stopLoop('sfx-engine');
        this.onArrivedAtDelivery();
      },
    });
  }

  onArrivedAtDelivery() {
    this.setPhase(PHASE.DELIVERED);
    gameState.delivered += 1;
    gameState.roundIndex += 1;
    if (gameState.roundIndex >= gameState.totalRounds) {
      this.time.delayedCall(300, () => this.endGame());
    } else {
      this.time.delayedCall(300, () => this.transitionToNextRound());
    }
  }

  transitionToNextRound() {
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.truckBedContainer.setAlpha(1);
      this.startRound();
      this.cameras.main.fadeIn(500, 0, 0, 0);
    });
  }

  endGame() {
    // Quiet the in-game bed + any lingering loops so the EndScene reward
    // fanfare comes through clean.
    if (this.bgMusic && this.bgMusic.isPlaying) this.bgMusic.stop();
    this.stopLoop('sfx-engine');
    this.stopLoop('sfx-conveyor');
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('EndScene');
    });
  }

  // ────────────────────────────────────────────────────────────────────
  // Update — input + driving + conveyor scroll
  // ────────────────────────────────────────────────────────────────────
  update(_time, dt) {
    if (this.cursors) {
      if (this.cursors.right.isDown) this.driveDirection = 1;
      else if (this.cursors.left.isDown) this.driveDirection = -1;
      else if (!this.input.activePointer.isDown) this.driveDirection = 0;
    }

    // Belt scrolls at exactly the same rate the crates move. dt is in ms,
    // CONVEYOR_SPEED is px/s, so per-frame delta = dt/1000 * speed. We
    // negate to make the texture appear to slide RIGHT (matching the crate
    // travel direction).
    if (
      this.conveyorActive &&
      this.conveyorBelt &&
      this.conveyorBelt.tilePositionX !== undefined &&
      this.interiorContainer.visible
    ) {
      this.conveyorBelt.tilePositionX -= (dt / 1000) * CONVEYOR_SPEED;
    }

    if (this.driveDirection !== 0 && !this.truckMoving) {
      this.handleDriveTick();
    }
  }

  handleDriveTick() {
    // Outdoor layout (bg-1): Cargo Delivery on the RIGHT. Truck enters from
    // the left and drives RIGHT to dock. So → drives toward delivery, ← is
    // a bounded reverse.
    if (this.phase === PHASE.PARKED_OUT && this.driveDirection > 0) {
      // Drive RIGHT to the dock → trigger interior loading
      this.driveDirection = 0;
      this.setPhase(PHASE.DRIVING_IN);
      this.driveTo(TRUCK_X_DOCKED, () => {
        // Truck has arrived → flip the parked text to enabled, then
        // hold long enough for the player to notice the green PARKED
        // state before we transition into the loading scene.
        this.setParked(true);
        this.time.delayedCall(1200, () => this.transitionToInterior());
      });
    } else if (this.phase === PHASE.PARKED_LOADED && this.driveDirection > 0) {
      // Drive RIGHT past dock → bg swap → exit
      this.driveDirection = 0;
      this.startDriveOut();
    } else if (this.phase === PHASE.PARKED_LOADED && this.driveDirection < 0) {
      // Bounded reverse on bg-1 (back up a little toward dock)
      this.driveDirection = 0;
      const newX = Math.max(this.truck.x - 60, TRUCK_X_DOCKED - 40);
      this.driveTo(newX);
    }
    // Other phases: input ignored.
  }

  // ────────────────────────────────────────────────────────────────────
  // Layout helpers
  // ────────────────────────────────────────────────────────────────────
  fitInto(image, maxW, maxH) {
    const w = image.width || 1;
    const h = image.height || 1;
    const s = Math.min(maxW / w, maxH / h, 1);
    image.setScale(s);
  }
  fitCover(image, w, h) {
    const sx = w / (image.width || 1);
    const sy = h / (image.height || 1);
    image.setScale(Math.max(sx, sy));
  }
}
